import {
  Card,
  CardActions,
  CardContent,
  Collapse,
  createStyles,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { GetApp, FiberManualRecord, ExpandMore, ExpandLess, Info, ChevronRight } from '@material-ui/icons';
import { TimelineSeparator, TimelineItem, TimelineContent, TimelineConnector, Timeline } from '@material-ui/lab';
import {
  CertificadoRecebiveisApiResponse,
  DocumentTypeHistorico,
  DocumentHistorico,
  DetalhesHistorico,
  HistoricoTimeline,
  HistoricoDetalhes,
} from 'common/genericTypes';
import {
  getCertificadoRecebiveisLongDescription,
  getCertificadoRecebiveisShortDescription,
  documentTypeHistoricoDescriptions,
} from 'common/genericDescriptions';
import { MdSpace, SmSpace, TextButton, Button } from 'm2m.components';
import moment from 'moment';
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import _ from 'lodash';
import certificadoRecebiveisParser from 'utils/parsers/certificadoRecebiveisParser';

const useStyles = makeStyles(() =>
  createStyles({
    timelineItem: {
      '&:before': {
        display: 'none',
      },
    },
    timelineContent: {
      padding: '0px 16px',
    },
  })
);

interface Props {
  certificadoRecebiveis: CertificadoRecebiveisApiResponse;
}

const camposParaIgnorar = [
  'dataultimaalteracao',
  'registro',
  'instrumentofinanceiro',
  'custodianteid',
  'contadeposito',
];
const eventosDeCadastro = ['Incluir um CR transferido de outra DVM', 'Incluir um CR', 'Cadastro de CR'];

const Historico: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const getHistoricoApi = useSelector(
    (state: RootState) => state.certificadoRecebiveisApi.getHistoricoCertificadoRecebiveis
  );
  const getDocumentDownloadUrl = useSelector(
    (state: RootState) => state.certificadoRecebiveisApi.getCertificadoRecebiveisDocumentDownloadUrl
  );
  const { certificadoRecebiveis } = props;

  const classes = useStyles();
  const [historico, setHistorico] = useState<Array<HistoricoTimeline>>([]);
  const [expanded, setExpanded] = useState<string>('');

  const getHistorico = useCallback(async () => {
    const result = await getHistoricoApi(dispatch, certificadoRecebiveis.certificadoRecebiveisId, true);

    const tratado = result
      .map<DetalhesHistorico<HistoricoDetalhes>>((hist) => {
        return {
          usuarioId: hist.usuarioId,
          usuarioNome: hist.usuarioNome,
          data: moment(hist.data),
          descricaoDaAcao: hist.descricaoDaAcao,
          descricaoDoCampo: hist.descricaoDoCampo ?? '',
          nomeDoCampo: hist.nomeDoCampo ?? '',
          valorAntigo:
            hist.valorAntigo && hist.nomeDoCampo
              ? certificadoRecebiveisParser.formatarCampoAlteracoes(hist.nomeDoCampo, hist.valorAntigo)
              : '',
          valorNovo:
            hist.valorNovo && hist.nomeDoCampo
              ? certificadoRecebiveisParser.formatarCampoAlteracoes(hist.nomeDoCampo, hist.valorNovo)
              : '',
        };
      })
      .filter((detalhe) => !camposParaIgnorar.includes(detalhe.nomeDoCampo.toLocaleLowerCase()));
    const groupedResult = _.groupBy(tratado, (item) => item.data.format('dddd DD/MM/YYYY'));
    const timeline = Object.entries(groupedResult).map<HistoricoTimeline>((group) => {
      return { data: group[0], alteracoes: _.orderBy(group[1], (x) => x.data, 'desc') };
    });

    setHistorico(_.orderBy(timeline, (x) => moment(x.data), 'desc'));
  }, [certificadoRecebiveis]);

  const getAttachmentLink = async (documentId: string, name: string) => {
    try {
      const result = await getDocumentDownloadUrl(dispatch, certificadoRecebiveis.certificadoRecebiveisId, documentId);

      if (result) {
        const link = document.createElement('a');
        link.href = result;
        link.target = '_blank';
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleToggleMoreDetails = (key: string) => setExpanded(expanded === key ? '' : key);

  useEffect(() => {
    getHistorico();
  }, [getHistorico]);

  const cardDetalhesDocumentos = (antigos: Array<DocumentHistorico>, novos: Array<DocumentHistorico>) => {
    let tipo = DocumentTypeHistorico.AvisosOfertaPublica;

    const diffAntigo = antigos.filter(
      (x) =>
        antigos.filter((a) => a.EmissionType === x.EmissionType).length !==
        novos.filter((n) => n.EmissionType === x.EmissionType).length
    );
    const diffNovo = novos.filter(
      (x) =>
        antigos.filter((a) => a.EmissionType === x.EmissionType).length !==
        novos.filter((n) => n.EmissionType === x.EmissionType).length
    );

    if (diffAntigo.length > 0) tipo = diffAntigo[0].EmissionType;
    else if (diffNovo.length > 0) tipo = diffNovo[0].EmissionType;

    return (
      <CardContent style={{ paddingTop: 0 }}>
        <Divider />
        <SmSpace />
        <Typography display={'inline'} variant="body2">
          {'Alteração: '}
        </Typography>
        &nbsp;
        <Typography display={'inline'}>{documentTypeHistoricoDescriptions[tipo]}</Typography>
        <MdSpace />
        <Grid container alignItems={'flex-start'}>
          <Grid item xl={5}>
            {diffAntigo.map((antigo) => (
              <Button
                key={antigo.Id}
                style={{ height: 'fit-content' }}
                endIcon={<GetApp />}
                onClick={() => getAttachmentLink(antigo?.Id ?? '', antigo?.FileName ?? '')}
                variant="text"
                fullWidth
              >
                {antigo.FileName} - {`${Math.round((antigo.Size / 1000000) * 100) / 100}MB`}
              </Button>
            ))}
          </Grid>
          <Grid item xl={1} style={{ display: 'flex' }}>
            <ChevronRight />
          </Grid>
          <Grid item xl={5}>
            {diffNovo.map((novo) => (
              <Button
                key={novo.Id}
                style={{ height: 'fit-content' }}
                endIcon={<GetApp />}
                onClick={() => getAttachmentLink(novo?.Id ?? '', novo?.FileName ?? '')}
                variant="text"
                fullWidth
              >
                {novo.FileName} - {`${Math.round((novo.Size / 1000000) * 100) / 100}MB`}
              </Button>
            ))}
          </Grid>
        </Grid>
      </CardContent>
    );
  };

  return (
    <>
      <Timeline>
        {historico &&
          historico.map(({ data, alteracoes }) => (
            <React.Fragment key={data}>
              <TimelineItem className={classes.timelineItem}>
                <TimelineSeparator>
                  <FiberManualRecord style={{ margin: 2 }} fontSize="small" color="primary" />
                  <TimelineConnector style={{ width: 1, backgroundColor: '#E5E5E5' }} />
                </TimelineSeparator>
                <TimelineContent className={classes.timelineContent}>
                  <Typography variant="body2">{data}</Typography>
                </TimelineContent>
              </TimelineItem>
              {alteracoes.map((alteracao, index) => (
                <TimelineItem className={classes.timelineItem} key={index}>
                  <TimelineSeparator>
                    <TimelineConnector style={{ width: 1, backgroundColor: '#E5E5E5' }} />
                    <Info />
                    <TimelineConnector style={{ width: 1, backgroundColor: '#E5E5E5' }} />
                  </TimelineSeparator>
                  <TimelineContent className={classes.timelineContent}>
                    <Card variant="outlined" style={{ maxWidth: '40%', margin: '10px 0' }}>
                      {eventosDeCadastro.includes(alteracao.descricaoDaAcao) ? (
                        <CardContent style={{ paddingBottom: 16 }}>
                          <Typography display={'inline'}>{alteracao.usuarioNome}</Typography>&nbsp;
                          <Typography variant="body2" display={'inline'}>
                            {'cadastrou o CR'}
                          </Typography>
                          &nbsp;
                          <Typography display={'inline'}>
                            {`${moment(alteracao.data).format('DD/MM/YYYY')} às ${moment(alteracao.data).format(
                              'HH:mm'
                            )}`}
                          </Typography>
                        </CardContent>
                      ) : (
                        <CardContent style={{ paddingBottom: 16 }}>
                          <Typography display={'inline'}>{alteracao.usuarioNome}</Typography>&nbsp;
                          <Typography variant="body2" display={'inline'}>
                            {'alterou'}
                          </Typography>
                          &nbsp;
                          <Typography display={'inline'}>
                            {`${getCertificadoRecebiveisShortDescription(alteracao.nomeDoCampo)}`}
                          </Typography>
                          &nbsp;
                          <Typography variant="body2" display={'inline'}>
                            {'em'}
                          </Typography>
                          &nbsp;
                          <Typography display={'inline'}>
                            {`${moment(alteracao.data).format('DD/MM/YYYY')} às ${moment(alteracao.data).format(
                              'HH:mm'
                            )}`}
                          </Typography>
                        </CardContent>
                      )}
                      <>
                        <Collapse in={expanded === alteracao.nomeDoCampo + index} timeout="auto" unmountOnExit>
                          {alteracao.nomeDoCampo === 'Documents' ? (
                            cardDetalhesDocumentos(
                              alteracao.valorAntigo as Array<DocumentHistorico>,
                              alteracao.valorNovo as Array<DocumentHistorico>
                            )
                          ) : (
                            <CardContent style={{ paddingTop: 0 }}>
                              <Divider />
                              <SmSpace />
                              <Typography display={'inline'} variant="body2">
                                {'Alteração: '}
                              </Typography>
                              &nbsp;
                              <Typography display={'inline'}>
                                {getCertificadoRecebiveisLongDescription(alteracao.nomeDoCampo)}
                              </Typography>
                              <MdSpace />
                              <Grid container justifyContent={'center'} alignItems={'flex-start'}>
                                <Grid item xl={5}>
                                  <Typography>{alteracao.valorAntigo}</Typography>
                                </Grid>
                                <Grid item xl={1} style={{ display: 'flex' }}>
                                  <ChevronRight />
                                </Grid>
                                <Grid item xl={5}>
                                  <Typography>{alteracao.valorNovo}</Typography>
                                </Grid>
                              </Grid>
                            </CardContent>
                          )}
                        </Collapse>
                        {!eventosDeCadastro.includes(alteracao.descricaoDaAcao) && (
                          <CardActions style={{ padding: 10, display: 'flex', justifyContent: 'flex-end' }}>
                            <TextButton
                              aria-expanded={expanded === alteracao.nomeDoCampo + index}
                              size="small"
                              onClick={() => handleToggleMoreDetails(alteracao.nomeDoCampo + index)}
                              endIcon={expanded === alteracao.nomeDoCampo + index ? <ExpandLess /> : <ExpandMore />}
                            >
                              {expanded === alteracao.nomeDoCampo + index ? 'Menos Informações' : 'Mais Informações'}
                            </TextButton>
                          </CardActions>
                        )}
                      </>
                    </Card>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </React.Fragment>
          ))}
      </Timeline>
    </>
  );
};

export default Historico;
